export const COLOR_PALETTES = {
  semantic: ['#1d4ed8', '#047857', '#b45309', '#be123c', '#6d28d9', '#0f766e', '#4338ca', '#c2410c'],
  emerald: ['#047857', '#059669', '#10b981', '#065f46', '#0f766e', '#115e59'],
  rose: ['#be123c', '#e11d48', '#f43f5e', '#9f1239', '#c026d3', '#a21caf'],
  ocean: ['#1d4ed8', '#2563eb', '#0e7490', '#0369a1', '#4338ca', '#0f766e'],
  sunset: ['#c2410c', '#b45309', '#ca8a04', '#be123c', '#a16207', '#9a3412'],
  violet: ['#6d28d9', '#7c3aed', '#9333ea', '#4f46e5', '#581c87', '#4338ca'],
  custom: ['#1d4ed8', '#047857', '#b45309', '#6d28d9', '#be123c', '#0f766e'],
}

function parseIsoDate(value) {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  }

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function toDisplayLabel(value) {
  if (!value) return ''
  return String(value).replace(/^.*\[/, '').replace(/\]$/, '')
}

export function humanize(value) {
  return toDisplayLabel(String(value || '').replace(/_/g, ' '))
}

export function getFieldKeys(data) {
  return Object.keys(data?.[0] || {})
}

export function isNumberLike(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) return true
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return true
  return false
}

export function getNumericKeys(data) {
  const firstRows = (data || []).slice(0, 20)
  const keys = getFieldKeys(firstRows)
  return keys.filter((key) => firstRows.some((row) => isNumberLike(row[key])))
}

export function getCategoryKeys(data) {
  const numeric = new Set(getNumericKeys(data))
  return getFieldKeys(data).filter((key) => !numeric.has(key))
}

export function promptToTitle(prompt) {
  return (prompt || '').trim().replace(/\s+/g, ' ').slice(0, 72)
}

export function formatMetric(value) {
  if (value === null || value === undefined || value === '') return '—'
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return String(value)
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(numeric)
}

export function looksLikePercentColumn(column, formatString = '', columnType = '') {
  const text = `${String(column || '')} ${String(formatString || '')}`.toLowerCase()
  return text.includes('%') || text.includes('percent') || text.includes('compliance') || text.includes('coverage') || text.includes('growth') || text.includes('achivement') || text.includes('achievement')
}

export function looksLikeSalesValueColumn(column) {
  const label = String(column || '').toLowerCase()
  return (
    label.includes('sales') ||
    label.includes('sale') ||
    label.includes('budget') ||
    label.includes('target')
  ) && !looksLikePercentColumn(label)
}

export function scaleSalesValue(value, unit = 'absolute') {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return value
  if (unit === 'lakh') return numeric / 100000
  if (unit === 'cr') return numeric / 10000000
  return numeric
}

export function looksLikeDateColumn(column, columnType = '') {
  const label = String(column || '').toLowerCase()
  const type = String(columnType || '').toLowerCase()
  return type.includes('date') || label.includes('date') || label === 'month' || label === 'year' || label.includes('fiscal month') || label.includes('fiscal year')
}

export function normalizePercentNumber(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return value
  return Math.abs(numeric) <= 1.5 ? numeric * 100 : numeric
}

export function formatDateValue(value) {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = parseIsoDate(value)
  if (!parsed) return String(value)
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(parsed)
}

export function formatCellValue(value, column, columnFormats = {}, columnTypes = {}, salesUnit = 'absolute') {
  if (value === null || value === undefined || value === '') return '—'
  if (looksLikeDateColumn(column, columnTypes[column])) return formatDateValue(value)
  if (looksLikePercentColumn(column, columnFormats[column], columnTypes[column]) && isNumberLike(value)) {
    return `${normalizePercentNumber(value).toFixed(2)}%`
  }
  if (looksLikeSalesValueColumn(column) && isNumberLike(value)) return formatMetric(scaleSalesValue(value, salesUnit))
  if (isNumberLike(value)) return formatMetric(value)
  return String(value)
}

export function getChartColors(config, count = 8) {
  const base =
    config.paletteName === 'custom'
      ? [
          config.primaryColor,
          config.secondaryColor,
          config.accentColor,
          config.primaryColor,
          config.secondaryColor,
          config.accentColor,
        ]
      : COLOR_PALETTES[config.paletteName] || COLOR_PALETTES.semantic

  return Array.from({ length: count }, (_, index) => base[index % base.length])
}

export function createWaterfallData(rows) {
  let cumulative = 0
  return rows.map((row) => {
    const start = cumulative
    cumulative += row.value
    return {
      ...row,
      invisible: start,
      visible: row.value,
      cumulative,
    }
  })
}

export function inferInitialViewFromResponse(response) {
  return response?.visualization?.resolved_view || 'table'
}

export function createDefaultConfig(data, prompt = '', response = null) {
  const fieldKeys = getFieldKeys(data)
  const numericKeys = getNumericKeys(data)
  const categoryKeys = getCategoryKeys(data)
  const categoryField = categoryKeys[0] || fieldKeys[0] || ''
  const valueField = numericKeys[0] || fieldKeys[0] || ''
  const compareField = numericKeys[1] || valueField
  const xField = numericKeys[0] || valueField
  const yField = numericKeys[1] || valueField
  const sizeField = numericKeys[2] || numericKeys[0] || valueField
  const lineField = numericKeys[1] || numericKeys[0] || valueField

  return {
    title: response?.visualization?.title ? promptToTitle(response.visualization.title) : promptToTitle(prompt || 'Query result'),
    prompt,
    viewType: inferInitialViewFromResponse(response),
    showTooltip: true,
    showLabels: true,
    showLegend: false,
    showAxis: true,
    orientation: 'vertical',
    categoryField,
    valueField,
    compareField,
    lineField,
    sortByField: valueField,
    xField,
    yField,
    sizeField,
    innerRadius: 44,
    outerRadius: 96,
    paddingAngle: 2,
    labelOffset: 12,
    barSize: 22,
    paletteName: 'semantic',
    primaryColor: '#1d4ed8',
    secondaryColor: '#047857',
    accentColor: '#b45309',
    salesUnit: 'absolute',
    panelOpen: false,
    analysisTab: 'summary',
    columnFormats: response?.data?.column_formats || {},
    columnTypes: response?.data?.column_types || {},
  }
}

export function normalizeChartData(data, config) {
  return [...data]
    .map((item, index) => {
      const rawValue = item[config.valueField]
      const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue)
      const chartValue = looksLikeSalesValueColumn(config.valueField) ? scaleSalesValue(numericValue, config.salesUnit) : numericValue

      return {
        id: `${String(item[config.categoryField] ?? index)}-${index}`,
        name: String(item[config.categoryField] ?? `Row ${index + 1}`),
        value: Number.isFinite(chartValue)
          ? looksLikePercentColumn(config.valueField, config.columnFormats?.[config.valueField], config.columnTypes?.[config.valueField])
            ? normalizePercentNumber(chartValue)
            : chartValue
          : 0,
        compare: (() => {
          const raw = Number(item[config.compareField])
          if (!Number.isFinite(raw)) return 0
          const scaled = looksLikeSalesValueColumn(config.compareField) ? scaleSalesValue(raw, config.salesUnit) : raw
          return looksLikePercentColumn(config.compareField, config.columnFormats?.[config.compareField], config.columnTypes?.[config.compareField]) ? normalizePercentNumber(scaled) : scaled
        })(),
        line: (() => {
          const raw = Number(item[config.lineField])
          if (!Number.isFinite(raw)) return 0
          const scaled = looksLikeSalesValueColumn(config.lineField) ? scaleSalesValue(raw, config.salesUnit) : raw
          return looksLikePercentColumn(config.lineField, config.columnFormats?.[config.lineField], config.columnTypes?.[config.lineField]) ? normalizePercentNumber(scaled) : scaled
        })(),
        x: Number(item[config.xField]) || 0,
        y: Number(item[config.yField]) || 0,
        size: Number(item[config.sizeField]) || 1,
        original: item,
      }
    })
    .sort((a, b) => {
      const aVal = a.original[config.sortByField]
      const bVal = b.original[config.sortByField]
      if (typeof aVal === 'number' && typeof bVal === 'number') return bVal - aVal
      if (isNumberLike(aVal) && isNumberLike(bVal)) return Number(bVal) - Number(aVal)
      return String(aVal).localeCompare(String(bVal))
    })
}

export function validateSemanticChartConfig(data, config, viewType = config?.viewType) {
  const rows = Array.isArray(data) ? data : []
  const fields = new Set(getFieldKeys(rows))
  const numericFields = new Set(getNumericKeys(rows))
  const missing = []
  const nonNumeric = []

  const requireField = (field, label, numeric = false) => {
    if (!field || !fields.has(field)) {
      missing.push(label)
      return
    }
    if (numeric && !numericFields.has(field)) nonNumeric.push(label)
  }

  if (!rows.length) return { ok: false, message: 'No rows returned for this query.' }
  if (viewType === 'table') return { ok: true, message: '' }

  requireField(config.categoryField, 'Category')
  if (['bar', 'area', 'pie', 'donut', 'rose', 'donutRose', 'treemap', 'waterfall', 'combo', 'stackedBar'].includes(viewType)) {
    requireField(config.valueField, 'Value', true)
  }
  if (['line', 'combo'].includes(viewType)) requireField(config.lineField, 'Line field', true)
  if (viewType === 'stackedBar') requireField(config.compareField, 'Compare field', true)
  if (viewType === 'quadrant') {
    requireField(config.xField, 'X field', true)
    requireField(config.yField, 'Y field', true)
    requireField(config.sizeField, 'Size field', true)
  }

  if (missing.length) return { ok: false, message: `Select valid ${missing.join(', ')} mapping before viewing this chart.` }
  if (nonNumeric.length) return { ok: false, message: `${nonNumeric.join(', ')} must be numeric for this chart.` }
  return { ok: true, message: '' }
}

export function suggestionToken(message) {
  const trimmed = message.trimEnd()
  if (!trimmed) return ''
  const parts = trimmed.split(/\s+/)
  return parts[parts.length - 1] || ''
}

export function applySuggestion(message, suggestion) {
  const trimmedRight = message.replace(/\s+$/, '')
  const pieces = trimmedRight.split(/\s+/)
  pieces.pop()
  const prefix = pieces.length ? `${pieces.join(' ')} ` : ''
  return `${prefix}${suggestion.insert_text}`
}

export function parseCommaValues(value) {
  return value.split(',').map((item) => item.trim()).filter(Boolean)
}
